import { FacilityDevicesByFloorsQueryResult } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

import { DeviceI, FloorI, TailgateConnectionI } from '../types/types'
import { DEFAULT_CAMERA_PROPERTIES } from './defaultCameraProperties'

type DeviceNodeType =
  | FacilityDevicesByFloorsQueryResult['data']['facility']['devices']['edges'][0]['node']

type FacilityType = Pick<
  FacilityDevicesByFloorsQueryResult['data']['facility'],
  'id' | 'name'
>

/**
 * Association maps for device relationships.
 */
interface DeviceAssociationMapsI {
  /** Map from door ID to set of camera IDs connected to that door */
  doorToCameras: Map<string, Set<string>>
  /** Map from camera ID to set of door IDs connected to that camera */
  cameraToDoors: Map<string, Set<string>>
}

interface BuildDeviceAssociationMapsOptionsI {
  /**
   * Resolve a door's cameras from the connection's own `cameraIds` rather than only by
   * inverting each camera's connections. Opt in, so the association maps stay unchanged for
   * callers that predate `cameraIds` being mapped.
   */
  shouldUseConnectionCameraIds?: boolean
}

export const getDeviceFloorIds = (
  selectedFloorId: string,
  floors: FloorI[],
  types: DeviceType[]
): string[] => {
  if (types.includes(DeviceType.Checkpoint)) {
    return floors?.map((f) => f.id)
  }
  return [selectedFloorId]
}

export const mapDevices = (
  data: FacilityDevicesByFloorsQueryResult['data']
): DeviceI[] =>
  data?.facility?.devices?.edges?.map((e) => mapDevice(e?.node, data?.facility))

export const mapDevice = (
  deviceNode: DeviceNodeType,
  facility: FacilityType
): DeviceI => ({
  x: deviceNode.x || DEFAULT_CAMERA_PROPERTIES.x,
  y: deviceNode.y || DEFAULT_CAMERA_PROPERTIES.y,
  rotation: deviceNode.bearing || DEFAULT_CAMERA_PROPERTIES.rotation,
  angle: deviceNode.fieldOfView || DEFAULT_CAMERA_PROPERTIES.angle,
  distance: deviceNode.visibilityRange || DEFAULT_CAMERA_PROPERTIES.distance,
  id: deviceNode.id,
  deviceName: deviceNode.name,
  isEditMode: false,
  editCheckpointPos: false,
  deviceHealth: deviceNode.status,
  placed: deviceNode.x != null && deviceNode.y != null,
  deviceType: deviceNode.type,
  tailgateConnections: deviceNode?.tailgateConns?.edges?.map((edge) => ({
    id: edge.node.id,
    door: edge.node.door.id,
    cameraIds: edge.node.cameras?.edges
      ?.map((cameraEdge) => cameraEdge?.node?.id)
      ?.filter(Boolean),
  })) as TailgateConnectionI[],
  createdAt: deviceNode.createdAt,
  camera: deviceNode.camera,
  checkpoint: {
    id: deviceNode?.checkpoint?.id,
    displayId: deviceNode?.checkpoint?.displayId,
    isPhotoRequired: deviceNode?.checkpoint?.isPhotoRequired,
    facility: {
      id: facility?.id,
      name: facility?.name,
    },
    floor: {
      id: deviceNode?.floor?.id,
      name: deviceNode?.floor?.name,
    },
  },
})

/**
 * Builds bidirectional index maps for device associations via tailgate connections.
 * This enables O(1) lookups for finding associated devices instead of O(n) searches.
 *
 * @param devices - Array of all devices
 * @returns Object containing doorToCameras and cameraToDoors maps
 */
export const buildDeviceAssociationMaps = (
  devices: DeviceI[],
  options?: BuildDeviceAssociationMapsOptionsI
): DeviceAssociationMapsI => {
  const doorToCameras = new Map<string, Set<string>>()
  const cameraToDoors = new Map<string, Set<string>>()

  const addAssociation = (cameraId: string, doorId: string) => {
    if (!cameraToDoors.has(cameraId)) {
      cameraToDoors.set(cameraId, new Set())
    }
    cameraToDoors.get(cameraId)!.add(doorId)

    if (!doorToCameras.has(doorId)) {
      doorToCameras.set(doorId, new Set())
    }
    doorToCameras.get(doorId)!.add(cameraId)
  }

  devices.forEach((device) => {
    if (device.tailgateConnections) {
      device.tailgateConnections.forEach((conn) => {
        if (conn.door) {
          if (
            options?.shouldUseConnectionCameraIds &&
            conn.door === device.id
          ) {
            // This device is the door side of the connection, so its cameras are named on the
            // connection itself. Reading them here means a door's cameras resolve even when
            // those camera devices are absent from `devices`.
            conn.cameraIds?.forEach((cameraId) => {
              if (cameraId && cameraId !== device.id) {
                addAssociation(cameraId, device.id)
              }
            })
            return
          }

          // This device (camera) connects to a door, and the door connects back to it
          addAssociation(device.id, conn.door)
        }
      })
    }
  })

  return { doorToCameras, cameraToDoors }
}

/**
 * Filters devices to include those matching the name filter and their associated devices.
 * Associated devices are determined through tailgate connections (cameras ↔ doors).
 * Uses index maps for O(n) performance instead of O(n²).
 *
 * @param devices - Array of all devices
 * @param nameLikeFilter - Name filter string
 * @returns Filtered array of devices including matches and associated devices
 */
export const filterDevicesWithAssociations = (
  devices: DeviceI[],
  nameLikeFilter: string,
  options?: BuildDeviceAssociationMapsOptionsI
): DeviceI[] => {
  if (!nameLikeFilter) {
    return devices
  }

  // Build index maps for O(1) lookups - O(n) time
  const { doorToCameras, cameraToDoors } = buildDeviceAssociationMaps(
    devices,
    options
  )

  // Find devices matching the name filter - O(n) time
  const matchingDevices = devices.filter((device) =>
    device.deviceName?.toLowerCase().includes(nameLikeFilter.toLowerCase())
  )

  const associatedDeviceIds = new Set<string>()

  // For each matching device, add it and its associations - O(m * k) time
  // where m = number of matching devices, k = average associations per device
  matchingDevices.forEach((device) => {
    // Add the matching device itself
    associatedDeviceIds.add(device.id)

    // Add associated cameras if this is a door - O(k) time via map lookup
    const connectedCameras = doorToCameras.get(device.id)
    if (connectedCameras) {
      connectedCameras.forEach((cameraId) => {
        associatedDeviceIds.add(cameraId)
      })
    }

    // Add associated doors if this is a camera - O(k) time via map lookup
    const connectedDoors = cameraToDoors.get(device.id)
    if (connectedDoors) {
      connectedDoors.forEach((doorId) => {
        associatedDeviceIds.add(doorId)
      })
    }
  })

  // Filter to only include devices that match or are associated - O(n) time
  return devices.filter((device) => associatedDeviceIds.has(device.id))
}

/**
 * Folds a freshly read device list into the one the canvas is already showing.
 *
 * A plain replace is what made a side-panel tab switch look like it discarded a drag
 * (ENG-31715): the server copy is only as fresh as the last read, so it happily reverts
 * a placement the user has just made. Two things survive the fold:
 *
 * - placement of a device in `locallyOwnedIds` — one being dragged, or whose write is
 *   still in flight. The user's hand is on it; the server's copy is behind by definition.
 * - the transient edit flags, for every device. Refreshes are frequent enough that
 *   clearing them would close an open popover out from under the user.
 *
 * Every other field is taken from `incoming`, so renames, health and association changes
 * still land.
 */
export const reconcileDevices = (
  current: DeviceI[],
  incoming: DeviceI[],
  locallyOwnedIds: Set<string>
): DeviceI[] => {
  if (current.length === 0) return incoming

  const currentById = new Map(current.map((device) => [device.id, device]))

  return incoming.map((device) => {
    const existing = currentById.get(device.id)
    if (!existing) return device

    // Selection/edit affordances belong to the canvas, not to the server.
    const merged: DeviceI = {
      ...device,
      isEditMode: existing.isEditMode,
      editCheckpointPos: existing.editCheckpointPos,
    }

    if (!locallyOwnedIds.has(device.id)) return merged

    return {
      ...merged,
      x: existing.x,
      y: existing.y,
      rotation: existing.rotation,
      angle: existing.angle,
      distance: existing.distance,
      placed: existing.placed,
    }
  })
}
